import { getGoiGroupVersions, getGois } from '@/generated/client/goi/goi';
import { FeatureCollectionDto, GoiGroupDto } from '@/generated/model';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

export default function useGois(selectedGoiGroups: GoiGroupDto[] | undefined) {
  const [gois, setGois] = useState<FeatureCollectionDto[] | undefined>(undefined);
  const prevGoisRef = useRef<Map<number, FeatureCollectionDto>>(new Map());
  const prevSelectedGoiGroups = useRef<GoiGroupDto[] | undefined>(undefined);
  const [addGoiIds, setAddGoiIds] = useState<number[]>([]);

  async function updateGois(changedGroupIds: number[]) {
    if (document.visibilityState !== 'visible') {
      return;
    }
    const prevGoisByGroupIds = prevGoisRef.current;
    if (!prevGoisByGroupIds.size) {
      return;
    }
    const changedGois = [...prevGoisByGroupIds.keys()].filter((groupId) => changedGroupIds.includes(groupId));

    if (!changedGois) {
      return;
    }
    const newGois = (await getGois({ groupIds: changedGois })).data;

    newGois.forEach((goi) => {
      prevGoisByGroupIds.set(goi.groupId, goi);
    });
    setGois([...prevGoisByGroupIds.values()]);
  }

  async function reloadGois() {
    const prevGoisByGroupIds = prevGoisRef.current;
    if (!prevGoisByGroupIds.size) {
      return;
    }
    const versions = (await getGoiGroupVersions({ groupIds: [...prevGoisByGroupIds.keys()] })).data;
    const versionsByIds = new Map<number, number>();
    versions.forEach((version) => {
      versionsByIds.set(version.id, version.version);
    });

    let newGois: FeatureCollectionDto[] = [];
    const changedGoiIds: number[] = [];
    [...prevGoisByGroupIds.values()].forEach((goi) => {
      const groupId = goi.groupId;
      const version = versionsByIds.get(groupId);
      if (!version) {
        return;
      }
      if (version == goi.version) {
        newGois.push(goi);
      } else {
        changedGoiIds.push(goi.groupId);
      }
    });
    if (changedGoiIds.length) {
      const changedGois = (await getGois({ groupIds: changedGoiIds })).data;
      newGois = [...newGois, ...changedGois];
    }
    prevGoisByGroupIds.clear();
    newGois.forEach((goi) => {
      prevGoisByGroupIds.set(goi.groupId, goi);
    });
    setGois([...prevGoisByGroupIds.values()]);
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reloadGois();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const eventSource = new EventSource('/api/v1.1/goi/listen');
    eventSource.addEventListener('goi-changed', (event) => {
      updateGois(event.data);
    });
    return () => {
      eventSource.close();
    };
  }, []);

  const { data: fetchedGois = [] } = useQuery({
    queryKey: ['groupIds', addGoiIds],
    queryFn: async () => {
      return (await getGois({ groupIds: addGoiIds })).data;
    },
    enabled: addGoiIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => {
    const prevGoisByGroupIds = prevGoisRef.current;
    fetchedGois.forEach((goi) => {
      prevGoisByGroupIds.set(goi.groupId, goi);
    });
    setGois([...prevGoisByGroupIds.values()]);
  }, [fetchedGois]);

  useEffect(() => {
    async function loadGois() {
      if (isEqual(prevSelectedGoiGroups.current, selectedGoiGroups)) {
        return;
      }
      prevSelectedGoiGroups.current = selectedGoiGroups;
      if (!selectedGoiGroups) {
        return;
      }
      const prevGoisByGroupIds = prevGoisRef.current;
      if (selectedGoiGroups.length == 0) {
        setGois(undefined);
        prevGoisByGroupIds.clear();
        return;
      }
      const groupIds = selectedGoiGroups.map((group) => group.id);
      const prevGroupIds = [...prevGoisByGroupIds.keys()];

      const added = groupIds.filter((n) => !prevGroupIds.includes(n));
      const removed = prevGroupIds.filter((n) => !groupIds.includes(n));

      removed.forEach((groupId) => {
        prevGoisByGroupIds.delete(groupId);
      });

      setGois([...prevGoisByGroupIds.values()]);
      setAddGoiIds(added);
    }
    loadGois();
  }, [selectedGoiGroups]);

  return gois;
}
